/**
 * Raw browser facts for the backend's checkout risk flags.
 *
 * This file only READS values and sends them as-is. It must never decide what
 * they mean (bot, incognito, ...) — all of that lives in bible-core
 * (`src/lib/riskFlags.ts`), so the rules and weights stay off the client.
 *
 *   wd — navigator.webdriver, 1/0
 *   ch — window.chrome exists, 1/0
 *   sq — navigator.storage.estimate().quota, bytes
 */
export type BrowserReadings = { wd?: number; ch?: number; sq?: number };

const QUOTA_TIMEOUT_MS = 300;

let cached: Promise<BrowserReadings> | undefined;

async function readStorageQuota(): Promise<number | undefined> {
  try {
    const estimate = navigator.storage?.estimate?.();
    if (!estimate) return undefined;
    const result = await Promise.race([
      estimate,
      new Promise<undefined>((resolve) =>
        setTimeout(() => resolve(undefined), QUOTA_TIMEOUT_MS),
      ),
    ]);
    return typeof result?.quota === "number" ? result.quota : undefined;
  } catch {
    return undefined;
  }
}

async function read(): Promise<BrowserReadings> {
  const readings: BrowserReadings = {};
  try {
    readings.wd = navigator.webdriver ? 1 : 0;
  } catch {
    // leave unset
  }
  try {
    readings.ch = (window as { chrome?: unknown }).chrome ? 1 : 0;
  } catch {
    // leave unset
  }
  const quota = await readStorageQuota();
  if (quota !== undefined) readings.sq = quota;
  return readings;
}

/** Read once per pageload; later upserts reuse the same values. Never rejects. */
export function getBrowserReadings(): Promise<BrowserReadings> {
  if (typeof window === "undefined") return Promise.resolve({});
  cached ??= read().catch(() => ({}));
  return cached;
}
